<template>
  <div ref="chartRef" class="chart"></div>
</template>

<script setup>
import * as echarts from "echarts";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  xData: {
    type: Array,
    default: () => [],
  },
  temperatureData: {
    type: Array,
    default: () => [],
  },
  humidityData: {
    type: Array,
    default: () => [],
  },
});

const chartRef = ref(null);
let chartInstance = null;

const option = {
  color: ["#11d6f0", "#11c3a4"],
  grid: { top: 72, left: 62, right: 62, bottom: 58 },
  legend: {
    top: 18,
    right: 70,
    icon: "circle",
    itemWidth: 16,
    itemHeight: 16,
    textStyle: { color: "#d8f2ff", fontSize: 24 },
    data: ["温度", "湿度"],
  },
  tooltip: {
    trigger: "axis",
    backgroundColor: "rgba(38, 73, 99, .88)",
    borderColor: "rgba(122, 206, 255, .6)",
    textStyle: { color: "#fff", fontSize: 24 },
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: [],
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(118, 184, 225, .35)" } },
    axisLabel: { color: "#d7ecff", fontSize: 22 },
  },
  yAxis: [
    {
      type: "value",
      name: "℃",
      min: 0,
      max: 50,
      nameTextStyle: { color: "#d7ecff", fontSize: 24, padding: [0, 36, 0, 0] },
      axisLabel: { color: "#d7ecff", fontSize: 22, formatter: (value) => (value === 0 ? "" : value) },
      splitLine: { lineStyle: { color: "rgba(117, 167, 206, .22)", type: "dashed" } },
    },
    {
      type: "value",
      name: "%",
      min: 0,
      max: 100,
      nameTextStyle: { color: "#d7ecff", fontSize: 24, padding: [0, 0, 0, 36] },
      axisLabel: { color: "#d7ecff", fontSize: 22, formatter: (value) => (value === 0 ? "" : value) },
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: "温度",
      type: "line",
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: 4 },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(17, 214, 240, .62)" },
          { offset: 1, color: "rgba(17, 214, 240, .03)" },
        ]),
      },
      data: [],
    },
    {
      name: "湿度",
      type: "line",
      smooth: true,
      yAxisIndex: 1,
      symbolSize: 0,
      lineStyle: { width: 4 },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(17, 195, 164, .66)" },
          { offset: 1, color: "rgba(17, 195, 164, .04)" },
        ]),
      },
      data: [],
    },
  ],
};

function resizeChart() {
  chartInstance && chartInstance.resize();
}

function updateChart() {
  if (!chartInstance) return;

  chartInstance.setOption({
    xAxis: {
      data: props.xData,
    },
    series: [
      {
        data: props.temperatureData,
      },
      {
        data: props.humidityData,
      },
    ],
  });
}

onMounted(() => {
  chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(option);
  updateChart();
  window.addEventListener("resize", resizeChart);
});

watch(
  () => [props.xData, props.temperatureData, props.humidityData],
  updateChart,
  { deep: true }
);

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chartInstance && chartInstance.dispose();
});
</script>

<style lang="less" scoped>
.chart {
  width: 100%;
  height: 100%;
}
</style>
